import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import GoogleSignIn from "@repo/ui/external/GoogleSignIn";
import { Delete, Get, Patch, Post } from "@repo/utils/api";
import EventEmitter from "@repo/utils/EventEmitter";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import User from "@repo/vygor-models/User";

import analytics from "~/analytics/client.js";

import { api } from "../index.jsx";

export { User };

export type Credentials = {
  email: string;
  password: string;
};

export type UserRest = Post<Credentials, { user: User }> &
  Get<{}, { user: User }> & {
    [id: User.Id]: Patch<User.Params, { user: User }>;
  };

export type SessionRest = Post<Credentials, { user: User }> & {
  "self": Delete;
  "google-sso-callback": Post<{ credential: string }, { user: User }>;
};

const initialState = {
  payload: null as User | null,
  authenticated: null as boolean | null,
};

export const { Context, Provider, useUser } = FeatureStore.init(
  "User",
  initialState,
  ([store, setStore], { withLogger, once }) => {
    const events = once(
      "events",
      () => new EventEmitter<[["login/signup", User]]>(),
    );

    const accessors = Object.assign(() => store.payload, {
      authenticated: () => store.authenticated,
      on: events.on,
    });

    const applyPayload =
      ({ user }: { user: User }, isFetch = false) =>
      () => {
        batch(() => {
          setStore("payload", reconcile(user));
          setStore("authenticated", true);
        });
        if (!isFetch) events.emit("login/signup", user);

        analytics.identify(user.id);

        return user;
      };

    const actions = withLogger("action", {
      fetch() {
        return api.user
          .GET({})
          .then((res) => {
            applyPayload(res, true)();
          })
          .catch((err) => {
            if (err.response?.status === 401) setStore("authenticated", false);
            else throw err;
          });
      },
      register: Object.assign(
        (credentials: Credentials) =>
          actions.register.defer(credentials).then((resolve) => resolve()),
        {
          defer: (credentials: Credentials) =>
            api.user.POST(credentials).then(applyPayload),
        },
      ),
      login: Object.assign(
        (credentials: Credentials) =>
          actions.login.defer(credentials).then((resolve) => resolve()),
        {
          defer: (credentials: Credentials) =>
            api.sessions.POST(credentials).then(applyPayload),
        },
      ),
      update(params: User.Params) {
        if (!store.payload)
          throw new Error("User must be authenticated to update.");
        return api.user[store.payload.id]!.PATCH(params).then(({ user }) => {
          setStore("payload", reconcile(user));
        });
      },
      googleSsoCallback: Object.assign(
        (credential: string) =>
          actions.googleSsoCallback
            .defer(credential)
            .then((resolve) => resolve()),
        {
          defer: (credential: string) =>
            api.sessions["google-sso-callback"]
              .POST({ credential })
              .then(applyPayload),
        },
      ),
      editUser(id: User.Id, params: User.Params) {
        return api.user[id]?.PATCH(params).then(({ user }) => {
          setStore("payload", reconcile(user));
        });
      },
      async logout() {
        setStore("authenticated", null);

        analytics.anonymize();

        GoogleSignIn.load.invalidate.all();
        await api.sessions.self.DELETE({});

        window.parent.postMessage(
          {
            from: "@vygor/embed",
            logout: true,
          },
          "*",
        );
        // TODO: Figure out why invalidate() unhooks feature reactivity
        window.location.reload();
        // invalidate();
      },
    });

    return { accessors, actions };
  },
);
